import { Text } from '@billda/design-system';
import { EventIcon } from '../atoms/EventIcon';
import { formatEventTime, formatEventDate } from '../../../utils/dateGrouping';
import type { BaseEventTimelineItem } from '../../../types/eventTimeline';

export interface EventRowProps {
  event: BaseEventTimelineItem;
}

/**
 * Single event row in the timeline
 * Displays event icon, description, details, notes, actor, and timestamp
 */
export function EventRow({ event }: EventRowProps) {
  return (
    <div
      data-testid="timeline-event"
      className="flex gap-4 rounded-lg px-4 py-4 transition-colors hover:bg-surface-raised"
    >
      {/* Event Icon */}
      <div className="shrink-0 pt-0.5">
        <EventIcon eventType={event.eventType} size="medium" />
      </div>

      {/* Event Content */}
      <div className="min-w-0 flex-1">
        {/* Description */}
        <Text
          variant="body2"
          data-testid="timeline-event-description"
          className="mb-0.5 font-semibold text-content-primary"
        >
          {event.description}
        </Text>

        {/* Details */}
        {event.details.length > 0 && (
          <Text variant="caption" color="secondary" className="mb-0.5 block">
            {event.details.join(' • ')}
          </Text>
        )}

        {/* Notes */}
        {event.notes && (
          <Text
            variant="caption"
            color="secondary"
            className="mb-0.5 block italic"
          >
            Note: {event.notes}
          </Text>
        )}

        {/* Actor and Timestamp */}
        <Text variant="caption" color="disabled" className="block">
          {event.actorUserName} • {formatEventTime(event.timestamp)} on{' '}
          {formatEventDate(event.timestamp)}
        </Text>
      </div>
    </div>
  );
}
